import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'lib/utils';

/**
 * renders a labeled text input with an inline error message, styled to match RadioFieldset
 * @param label - question shown above the input
 * @param name - input name, also used as its id
 * @param value - current input value
 * @param onChange - called with the new value
 * @param error - error message shown under the input, empty when the value is valid
 * @param placeholder - hint about the expected format
 * @param required - whether to show the required asterisk next to the label
 * @param type - html input type
 * @param inputMode - html input mode, e.g. numeric for number only fields
 * @param autoComplete - html autocomplete token
 * @param onBlur - called when the input loses focus
 * @param maxLength - hard limit of characters accepted by the input
 * @param autoFocus - focuses the input on mount
 */
export default function TextField({
  label,
  name,
  value,
  onChange,
  error = '',
  placeholder,
  required = true,
  type = 'text',
  inputMode,
  autoComplete,
  onBlur,
  maxLength,
  autoFocus = false
}) {
  return (
    <div className='min-w-0'>
      <label
        className='block text-sm font-semibold text-black mb-1.5'
        htmlFor={name}
      >
        {label}
        {required && <span className='text-semantic-danger'> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        maxLength={maxLength}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        className={cn(
          'w-full rounded-lg border border-grey-lighter bg-white px-3 py-2 text-black text-[length:--font-size-body] outline-none placeholder:text-grey-light focus:border-primary-emphasis',
          error && 'border-semantic-danger focus:border-semantic-danger'
        )}
      />
      {error && (
        <p className='mt-1 mb-0 text-xs leading-tight text-semantic-danger'>
          {error}
        </p>
      )}
    </div>
  );
}

TextField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  type: PropTypes.string,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
  onBlur: PropTypes.func,
  maxLength: PropTypes.number,
  autoFocus: PropTypes.bool
};
